// App
import Promise from 'promise';

// Actions
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import fetchNearbyAreasListings from 'app/shared/wrappers/FetchAreaWrapper/fetchNearbyAreasListings';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';

// Misc / Utils
import constants from 'app/shared/constants/ConstantsBundle';
import max from 'lodash/max';
import routeUtils from 'app/shared/utils/routeUtils';

// Types
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';

// Define interfaces for better type safety
interface Trace {
  startAsyncSpan: (params: { marker: string }) => void;
  finishAsyncSpan: (params: { marker: string }) => void;
}

interface ReduxStore {
  getState: () => RootReduxState;
  dispatch: ThunkDispatch<RootReduxState, undefined, AnyAction>;
}

interface RouteState {
  pathname: string;
  query: Record<string, string | number | boolean | undefined>;
}

interface ReactContext {
  store: ReduxStore;
  trace: Trace;
  state: RouteState;
}

interface ServerResponse {
  error?: boolean;
  changeHttpStatus?: number;
  redirect?: boolean;
  to?: string;
  [key: string]: unknown;
}

const resolveFetchListingsPromises = (
  reactContext: ReactContext,
  beforeFetchListings: Array<PromiseLike<ServerResponse>>,
): Promise<ServerResponse | void | undefined> => {
  const { store, trace } = reactContext;
  const { query } = reactContext.state;
  const state = store.getState();
  const isSmallScreen = state.app.device.screenWidth === 'sm';
  const hasLatLon = routeUtils.hasLatAndLon(query);

  return Promise.all(beforeFetchListings)
    .then((responses: Array<ServerResponse>) => {
      // check for bad area response
      for (const res of responses) {
        if (res && (res.error || res.changeHttpStatus)) {
          return res;
        }
      }

      if (isSmallScreen || !hasLatLon) {
        // HPWEB-3383 to prevent flickering due to inconsistent bbox on different
        // devices, don't ssr listings unless its small screen or doesn't have lat/lon/zoom
        const pageValue = typeof query.page === 'number' ? query.page : 0;
        const page = max([pageValue, 0]) ?? 0;
        const offset = constants.MAX_LISTINGS_PER_PAGE * (max([page - 1, 0]) ?? 0);

        trace.startAsyncSpan({
          marker: 'FetchAreaWrapper#fetchListingsByCoords',
        });
        // get new state because the filter has been updated since declaring state above
        const currState = store.getState();

        //HPWEB-5917 - state pages sorted by newest
        const areaType = currState?.area?.area?.type;
        if (areaType === 'state') {
          currState.filter.orderBy = 'activated';
        }
        //end HPWEB-5917
        return store
          .dispatch(
            ListingEngineActions.fetchListingsByCoords({
              mapData: null,
              filter: currState.filter,
              onMap: false,
              limit: constants.FETCH_LISTINGS_LIMIT,
              offset,
              shouldUseAreaBoundary: true,
            }),
          )
          .then((response: unknown) => {
            trace.finishAsyncSpan({
              marker: 'FetchAreaWrapper#fetchListingsByCoords',
            });

            return response;
          })
          .then(() => {
            const latestState = store.getState();
            const maxBuildings = latestState.listings.totalBuildings;
            const maxPage = Math.max(Math.ceil(maxBuildings / constants.MAX_LISTINGS_PER_PAGE), 1);
            const currentPage = latestState.location.current && latestState.location.current.query.page;
            const onLastPage = maxPage === Number(currentPage) || (maxPage === 1 && !currentPage);

            if (onLastPage) {
              trace.startAsyncSpan({ marker: 'FetchAreaWrapper#fetchNearbyAreasListings' });

              return fetchNearbyAreasListings(reactContext, {
                areaResourceId: latestState.area.area.resourceId,
              });
            }
            return undefined;
          }) as Promise<ServerResponse | void | undefined>;
      }
    })
    .catch((err) => {
      store.dispatch(
        ErrorActions.errorHandler({
          error: err,
          errorLocation: 'component.fetchAreaWrapper.serverRouteWillMount',
          errorClass: 'dataError',
          criticalType: 'dataError',
        }),
      );

      return { changeHttpStatus: 500 };
    });
};

export default resolveFetchListingsPromises;
